import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

/**
 * Shared meaning/definition formatting for Fill dialogs (Grid word search/hidden message and Crossword).
 * Keeps numbered list items (1., 2., … 10., 11.) on one row and splits definitions into one line per number.
 */

// Merge number+dot split across lines (e.g. "1\n0." -> "10.") so they stay on one row
export const mergeSplitDigits = (str) => {
    if (typeof str !== "string") return str;
    return str.replace(/(\d)((?:\s*[\r\n]+\s*\d)*)\s*\./g, (_, first, mid) => first + (mid || "").replace(/[\r\n\s]/g, "") + ".");
};

export const wrapNumbers = (str, keyPrefix = "") => {
    const parts = str.split(/(\d+\.)/g);
    return parts.map((p, i) =>
        /^\d+\.$/.test(p) ? <span key={`${keyPrefix}-${i}`} style={{ whiteSpace: "nowrap" }}>{p}</span> : <React.Fragment key={`${keyPrefix}-${i}`}>{p}</React.Fragment>
    );
};

// Split by numbered items (1., 2., … 10., 11.) so each row is "number. meaning"
export const formatDefinition = (def) => {
    if (typeof def !== "string") return def;
    const normalized = mergeSplitDigits(def);
    const parts = normalized.split(/\s+(?=\d+\.\s)/).map((s) => s.trim()).filter(Boolean);
    if (parts.length <= 1) return wrapNumbers(normalized, "single");
    return (
        <>
            {parts.map((part, i) => (
                <React.Fragment key={i}>
                    {i > 0 && <br />}
                    {wrapNumbers(part, `p${i}`)}
                </React.Fragment>
            ))}
        </>
    );
};

// Format a meaning object (e.g. { nom: "1. ... 2. ...", adjectif: "..." }) for display with part labels and definition formatting
export const formatMeaning = (meaning) => {
    if (!meaning || typeof meaning !== "object") return null;
    const entries = Object.entries(meaning).filter(([part]) => part.toLowerCase() !== "title");
    if (entries.length === 0) return null;
    return entries.map(([part, def]) => (
        <Box key={part} sx={{ mb: 0.5 }}>
            <Typography component="span" variant="body2" sx={{ fontWeight: 600, textTransform: "capitalize", color: "text.secondary" }}>{part}:</Typography>{" "}
            <Typography variant="body2" color="text.secondary" component="span">{typeof def === "string" ? formatDefinition(def) : JSON.stringify(def)}</Typography>
        </Box>
    ));
};
